import {
  ArcRotateCamera,
  Animation,
  CubicEase,
  EasingFunction,
  Mesh,
  Quaternion,
  Scene,
  Vector3,
} from "@babylonjs/core";
import { CAMERA_CONFIG } from "../../config/game.config";
import { CornerHint } from "../ui/CornerHint";

/**
 * PlayerCamera – owns the ArcRotateCamera and all pointer-lock / FP logic.
 *
 * Responsibilities:
 *  - Create and configure the ArcRotateCamera.
 *  - Handle pointer-lock acquisition / release.
 *  - Handle mouse-move rotation in pointer-lock mode.
 *  - Drive smooth camera-target lerp towards the player.
 *  - Toggle first-person / third-person mode based on zoom radius.
 *  - Play the cinematic intro zoom.
 */
export class PlayerCamera {
  readonly camera: ArcRotateCamera;
  private hint: CornerHint;
  private _isFirstPerson = false;
  /** Smoothed target position fed to camera.setTarget() every frame. */
  private target: Vector3;
  private canLock = true;

  constructor(
    private scene: Scene,
    startPos: Vector3
  ) {
    this.target = startPos.clone();

    this.camera = new ArcRotateCamera(
      "playerCamera",
      CAMERA_CONFIG.alpha,
      CAMERA_CONFIG.beta,
      CAMERA_CONFIG.radius,
      this.target,
      scene
    );

    this.camera.attachControl();
    this.camera.speed = CAMERA_CONFIG.speed;
    this.camera.wheelPrecision = CAMERA_CONFIG.wheelPrecision;
    this.camera.minZ = CAMERA_CONFIG.minZ;
    this.camera.lowerRadiusLimit = CAMERA_CONFIG.lowerRadiusLimit;
    this.camera.upperRadiusLimit = CAMERA_CONFIG.upperRadiusLimit;
    this.camera.lowerBetaLimit = CAMERA_CONFIG.lowerBetaLimit;
    this.camera.upperBetaLimit = CAMERA_CONFIG.upperBetaLimit;
    this.camera.checkCollisions = false;

    // Remove the default pointer-input so we control rotation ourselves.
    this.camera.inputs.removeByType("ArcRotateCameraPointersInput");

    this.hint = new CornerHint();
    this.setupPointerLock();
    this.playIntroAnimation();
  }

  // ── Pointer lock ────────────────────────────────────────────────────────────

  private setupPointerLock(): void {
    const canvas = this.scene.getEngine().getRenderingCanvas();
    if (!canvas) return;

    canvas.addEventListener("click", () => {
      if (this.canLock && !document.pointerLockElement) {
        canvas.requestPointerLock();
      }
    });

    document.addEventListener("pointerlockchange", () => {
      if (!document.pointerLockElement) {
        this.canLock = false;
        this.hint.showUnlocked();
        setTimeout(() => { this.canLock = true; }, 1000);
      } else {
        this.hint.showLocked();
      }
    });

    canvas.addEventListener("mousemove", (evt) => {
      if (document.pointerLockElement) {
        this.camera.alpha -= evt.movementX * CAMERA_CONFIG.rotationSensitivity;
        this.camera.beta -= evt.movementY * CAMERA_CONFIG.rotationSensitivity;
        // Clamp beta to prevent gimbal flip.
        this.camera.beta = Math.max(0.1, Math.min(Math.PI - 0.1, this.camera.beta));
      }
    });
  }

  // ── Cinematic intro ─────────────────────────────────────────────────────────

  private playIntroAnimation(): void {
    this.camera.radius = CAMERA_CONFIG.introStartRadius;

    const anim = new Animation(
      "introZoom",
      "radius",
      60,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    const ease = new CubicEase();
    ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    anim.setEasingFunction(ease);
    anim.setKeys([
      { frame: 0, value: CAMERA_CONFIG.introStartRadius },
      { frame: CAMERA_CONFIG.introDurationFrames, value: CAMERA_CONFIG.introTargetRadius },
    ]);
    this.camera.animations = [anim];
    this.scene.beginAnimation(this.camera, 0, CAMERA_CONFIG.introDurationFrames, false);
  }

  // ── First-person / Third-person ─────────────────────────────────────────────

  get isFirstPerson(): boolean {
    return this._isFirstPerson;
  }

  /**
   * Switch to first-person or third-person mode.
   * Hides / shows the player meshes and adjusts the camera radius.
   */
  setFirstPerson(fp: boolean, playerMeshes: Mesh[]): void {
    if (fp === this._isFirstPerson) return;
    this._isFirstPerson = fp;

    for (const mesh of playerMeshes) {
      mesh.isVisible = !fp;
    }
    if (fp) {
      this.camera.radius = 0;
      this.hint.showFirstPerson();
    } else {
      this.hint.showLocked();
    }
  }

  /**
   * Call every frame to:
   *  - smoothly lerp the camera target toward `playerPos`
   *  - detect FP/TP threshold crossing
   *  - align FP camera when in first-person mode
   */
  updatePerFrame(playerPos: Vector3, dt: number, playerMeshes: Mesh[], playerQuat: Quaternion | null): void {
    const { firstPersonThreshold, headOffset, upperRadiusLimit } = CAMERA_CONFIG;
    const smooth = Math.min(1, 5 * dt);

    // Zoom-to-head lerp: raise target Y as camera zooms in.
    const zoomFactor = this._isFirstPerson
      ? 1
      : 1 - Math.min(this.camera.radius / (upperRadiusLimit || 10), 1);
    const targetY = playerPos.y + headOffset * zoomFactor;

    this.target.x += (playerPos.x - this.target.x) * smooth;
    this.target.y += (targetY - this.target.y) * smooth;
    this.target.z += (playerPos.z - this.target.z) * smooth;
    this.camera.setTarget(this.target);

    // Detect FP/TP crossing.
    if (this.camera.radius < firstPersonThreshold && !this._isFirstPerson) {
      this.setFirstPerson(true, playerMeshes);
    } else if (this.camera.radius >= firstPersonThreshold && this._isFirstPerson) {
      this.setFirstPerson(false, playerMeshes);
    }

    // In FP mode, rotate the player model toward the camera direction.
    if (this._isFirstPerson && playerQuat) {
      const camForward = this.target.subtract(this.camera.position);
      camForward.y = 0;
      camForward.normalize();
      const fpAngle = Math.atan2(camForward.x, camForward.z);
      const targetQuat = Quaternion.RotationAxis(Vector3.Up(), fpAngle);
      Quaternion.SlerpToRef(playerQuat, targetQuat, Math.min(1, 10 * dt), playerQuat);
    }
  }

  dispose(): void {
    this.hint.dispose();
    this.camera.dispose();
  }
}
